/**
 * Band geometry for the assurance threshold editor.
 *
 * A card stores four thresholds plus a null catch-all, but what those four
 * numbers *mean* is five contiguous bands on an axis whose units change with
 * the calculation mode. These helpers turn the stored thresholds into that
 * axis and those bands so the editor can draw them, and back again.
 *
 * Ordering, gaps and mode-specific value checks stay in `validation.ts` —
 * this module only describes shape, it never decides validity.
 */
import type { BoundaryDirection, DataValueType, Level, LevelCalculationMode } from '../api/types';
import { parseThresholdInput } from './validation';

/** The four levels that actually carry a threshold value. */
export const CUT_LEVELS: Level[] = ['high', 'substantial', 'moderate', 'limited'];

export const BAND_LABELS: Record<Level, string> = {
  high: 'High',
  substantial: 'Substantial',
  moderate: 'Moderate',
  limited: 'Limited',
  none: 'None',
  nan: 'No data'
};

export interface BandConfig {
  thresholds: Record<Level, string>;
  mode: LevelCalculationMode;
  direction: BoundaryDirection;
  dataValueType: DataValueType;
}

export interface BandAxis {
  min: number;
  max: number;
  /** Suffix for displayed numbers — '%' for percentages and both deviation modes. */
  unit: string;
  /** True when a smaller comparison value is better. */
  ascending: boolean;
  /** Decimal places to show. */
  dp: number;
  /**
   * True when the axis is a two-sided tolerance around a base, so the editor
   * should mirror it. Only `deviation_bands` is.
   */
  mirrored: boolean;
}

export interface BandSpan {
  level: Level;
  lo: number;
  hi: number;
  size: number;
}

function thresholdNumbers(thresholds: Record<Level, string>): number[] {
  return CUT_LEVELS.map((level) => parseThresholdInput(thresholds[level])).filter(
    (value): value is number => value !== null
  );
}

/**
 * The axis the comparison value lives on.
 *
 * `deviation_bands` is pinned ascending to match the backend, which forces
 * ASCENDING regardless of the card's stored boundary_direction — see
 * `_get_assurance_level_from_context`.
 */
export function getBandAxis(config: BandConfig): BandAxis {
  const { thresholds, mode, direction, dataValueType } = config;
  const numbers = thresholdNumbers(thresholds);
  const highest = numbers.length > 0 ? Math.max(...numbers) : 100;
  const lowest = numbers.length > 0 ? Math.min(...numbers) : 0;
  const ascending = direction === 'ascending';

  if (mode === 'deviation_bands') {
    // Half-width: the drawn axis runs from -max to +max around the base.
    return { min: 0, max: Math.max(highest * 1.6, 1), unit: '%', ascending: true, dp: 1, mirrored: true };
  }
  if (mode === 'deviation_direct') {
    return {
      min: 0,
      max: Math.max(highest * 1.25, lowest + 40, 60),
      unit: '%',
      ascending,
      dp: 0,
      mirrored: false
    };
  }
  if (dataValueType === 'percentage') {
    return { min: 0, max: 100, unit: '%', ascending, dp: 0, mirrored: false };
  }
  // A plain number card has no natural ceiling, so leave headroom above the
  // widest threshold rather than inventing one. Floor at the lowest
  // threshold so a signed range (e.g. -20 to 50) is still on the axis.
  return {
    min: Math.min(lowest, 0),
    max: Math.max(highest * 1.15, 1),
    unit: '',
    ascending,
    dp: 0,
    mirrored: false
  };
}

/**
 * Each band as a span on the comparison axis, best first.
 *
 * A blank threshold collapses its band to zero width rather than shifting the
 * ones below it, so a half-filled set still draws something sensible.
 */
export function getBandSpans(config: BandConfig): BandSpan[] {
  const axis = getBandAxis(config);
  const raw: { level: Level; from: number; to: number }[] = [];
  let edge = axis.ascending ? axis.min : axis.max;

  for (const level of CUT_LEVELS) {
    const threshold = parseThresholdInput(config.thresholds[level]);
    const to = threshold === null ? edge : threshold;
    raw.push({ level, from: edge, to });
    edge = to;
  }
  raw.push({ level: 'none', from: edge, to: axis.ascending ? axis.max : axis.min });

  return raw.map(({ level, from, to }) => {
    const lo = Math.min(from, to);
    const hi = Math.max(from, to);
    return { level, lo, hi, size: Math.max(hi - lo, 0) };
  });
}

/**
 * Segments in the order they should be painted left to right.
 *
 * The bar is a number line: left is lower, right is higher. Higher-is-better
 * therefore puts High on the right; lower-is-better puts it on the left.
 * Close-to-base (`mirrored`) keeps High in the centre.
 */
export function getDrawnSegments(config: BandConfig): { level: Level; size: number }[] {
  const axis = getBandAxis(config);
  const spans = getBandSpans(config);
  if (axis.mirrored) {
    const outerSpans = spans.filter((span) => span.level !== 'high');
    return [
      ...[...outerSpans].reverse().map((span) => ({ level: span.level, size: span.size })),
      { level: 'high' as Level, size: spans[0].size * 2 },
      ...outerSpans.map((span) => ({ level: span.level, size: span.size }))
    ];
  }
  const ordered = axis.ascending ? spans : [...spans].reverse();
  return ordered.map((span) => ({ level: span.level, size: span.size }));
}

export function formatBandNumber(value: number | null, dp: number): string {
  if (value === null || !Number.isFinite(value)) {
    return 'n/a';
  }
  return value.toFixed(dp).replace(/\.0+$/, '');
}

/** Human-readable range for one band, e.g. '≥ 98%', '95% to 98%', 'within ±3%'. */
export function bandRangeText(config: BandConfig, span: BandSpan): string {
  const axis = getBandAxis(config);
  const at = (level: Level) => formatBandNumber(parseThresholdInput(config.thresholds[level]), axis.dp);
  const lastCut = [...CUT_LEVELS]
    .map((level) => parseThresholdInput(config.thresholds[level]))
    .filter((value): value is number => value !== null)
    .at(-1);

  // A ±% band is a two-sided tolerance and never reads as a ceiling.
  if (axis.mirrored) {
    if (span.level === 'high') return `within ±${at(span.level)}%`;
    if (span.level === 'none') {
      return lastCut === undefined ? '' : `past ±${formatBandNumber(lastCut, axis.dp)}%`;
    }
    return `±${formatBandNumber(span.lo, axis.dp)}% to ±${formatBandNumber(span.hi, axis.dp)}%`;
  }
  if (span.level === 'high') {
    return `${axis.ascending ? '≤' : '≥'} ${at(span.level)}${axis.unit}`;
  }
  if (span.level === 'none') {
    if (lastCut === undefined) return '';
    return `${axis.ascending ? 'over' : 'under'} ${formatBandNumber(lastCut, axis.dp)}${axis.unit}`;
  }
  return `${formatBandNumber(span.lo, axis.dp)}${axis.unit} to ` + `${formatBandNumber(span.hi, axis.dp)}${axis.unit}`;
}

export interface AppliedCut {
  thresholds: Record<Level, string>;
  /** Labels of the neighbouring levels that had to move, in the order they moved. */
  moved: string[];
}

/**
 * Set one threshold and push its neighbours only as far as they need to go.
 *
 * Ordering is the one rule a user can break by typing, and answering with a
 * validation panel after the fact is the worst moment to explain it. Nudging
 * instead makes an out-of-order set unreachable from the control, so the
 * panel's error block only ever reports thresholds that arrived from
 * elsewhere. `validateLevelBoundaries` remains the guard at save time.
 */
export function applyThresholdCut(
  thresholds: Record<Level, string>,
  level: Level,
  value: string,
  ascending: boolean
): AppliedCut {
  const next: Record<Level, string> = { ...thresholds, [level]: value };
  const index = CUT_LEVELS.indexOf(level);
  const moved: string[] = [];
  if (index === -1) {
    return { thresholds: next, moved };
  }

  const numberAt = (candidate: Level) => parseThresholdInput(next[candidate]);
  const outOfOrder = (better: Level, worse: Level) => {
    const a = numberAt(better);
    const b = numberAt(worse);
    if (a === null || b === null) return null;
    return ascending ? a > b : a < b;
  };

  for (let i = index - 1; i >= 0; i--) {
    if (outOfOrder(CUT_LEVELS[i], CUT_LEVELS[i + 1]) !== true) break;
    next[CUT_LEVELS[i]] = next[CUT_LEVELS[i + 1]];
    moved.push(BAND_LABELS[CUT_LEVELS[i]]);
  }
  for (let i = index + 1; i < CUT_LEVELS.length; i++) {
    if (outOfOrder(CUT_LEVELS[i - 1], CUT_LEVELS[i]) !== true) break;
    next[CUT_LEVELS[i]] = next[CUT_LEVELS[i - 1]];
    moved.push(BAND_LABELS[CUT_LEVELS[i]]);
  }

  return { thresholds: next, moved };
}
